import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { publicApi } from '../services/api';

const TRANSITION_STRATEGIES = {
  fade: {
    name: 'Desvanecer'
  },
  slide: {
    name: 'Deslizar'
  },
  zoom: {
    name: 'Zoom'
  },
  flip: {
    name: 'Voltear'
  }
};

const STORAGE_KEY = 'photobooth_display_settings';
const PRELOAD_BATCH_SIZE = 3;

const loadSettings = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch {
    return {};
  }
};

const saveSettings = (settings) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Ignore storage errors
  }
};

const PhotoBoothDisplay = () => {
  const { slug, token } = useParams();
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  
  const [projectInfo, setProjectInfo] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transitionStrategy, setTransitionStrategy] = useState('fade');
  const [slideDuration, setSlideDuration] = useState(5);
  const [imageFit, setImageFit] = useState('contain');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showTitle, setShowTitle] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const [preloadedImages, setPreloadedImages] = useState(new Set());
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  
  const slideshowIntervalRef = useRef(null);
  const eventSourceRef = useRef(null);
  const previousPhotosLengthRef = useRef(0);
  const preloadedImagesRef = useRef(new Set());
  const reconnectTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const videoTimeoutRef = useRef(null);
  
  const frontendUrl = process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3000';
  
  // En producción, usar la URL del API sin /api al final
  const getBackendUrl = () => {
    if (projectInfo?.backendUrl) return projectInfo.backendUrl;
    if (process.env.REACT_APP_BACKEND_URL) return process.env.REACT_APP_BACKEND_URL;
    // Derivar del API_URL removiendo /api
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    return apiUrl.replace(/\/api$/, '');
  };
  const backendUrl = getBackendUrl();

  const loadSettingsFromStorage = useCallback(() => {
    const settings = loadSettings();
    if (settings.showTitle !== undefined) setShowTitle(settings.showTitle);
    if (settings.showQR !== undefined) setShowQR(settings.showQR);
    if (settings.transitionStrategy) setTransitionStrategy(settings.transitionStrategy);
    if (settings.slideDuration) setSlideDuration(settings.slideDuration);
    if (settings.imageFit) setImageFit(settings.imageFit);
  }, []);

  const loadProjectInfo = useCallback(async () => {
    try {
      const response = token 
        ? await publicApi.get(`/photobooth/${slug}/tv/${token}`)
        : await publicApi.get(`/photobooth/${slug}/info`);
      
      if (response.data.success) {
        const info = token ? response.data.projectInfo : response.data.info;
        setProjectInfo(info);
      } else {
        setError(response.data.error || 'Token inválido o expirado');
      }
    } catch (err) {
      setError('Error al cargar el proyecto');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [slug, token]);

  const loadPhotos = useCallback(async () => {
    try {
      const response = await publicApi.get(`/photobooth/${slug}/photos?limit=50`);
      
      if (response.data.success) {
        const newPhotos = response.data.photos;
        const previousLength = previousPhotosLengthRef.current;
        
        if (newPhotos.length > previousLength && previousLength > 0) {
          setCurrentIndex(0);
          setAnimationKey(prev => prev + 1);
        }
        
        previousPhotosLengthRef.current = newPhotos.length;
        setPhotos(newPhotos);
        
        if (newPhotos.length > 0) {
          preloadImages(newPhotos, 0);
        }
      }
    } catch (err) {
      console.error('Error cargando fotos:', err);
    }
  }, [slug]);

  const preloadImages = useCallback((photoList, startIndex) => {
    const imagesToPreload = [];

    for (let i = 0; i < PRELOAD_BATCH_SIZE; i++) {
      const idx = (startIndex + i) % photoList.length;
      const photo = photoList[idx];
      if (photo && !preloadedImagesRef.current.has(photo.fileName)) {
        imagesToPreload.push(photo);
      }
    }

    imagesToPreload.forEach(photo => {
      const img = new Image();
      img.src = `${backendUrl}${photo.url}`;
      img.onload = () => {
        preloadedImagesRef.current.add(photo.fileName);
        if (isMountedRef.current) {
          setPreloadedImages(prev => new Set([...prev, photo.fileName]));
        }
      };
    });
  }, [backendUrl]);

  const handleNewPhoto = useCallback((photoData) => {
    console.log('📡 [SSE] Nueva foto recibida:', photoData);
    
    setPhotos(prevPhotos => {
      const exists = prevPhotos.some(p => p.fileName === photoData.fileName);
      if (exists) return prevPhotos;
      
      const newPhotos = [photoData, ...prevPhotos];
      previousPhotosLengthRef.current = newPhotos.length;
      return newPhotos;
    });
    
    setCurrentIndex(0);
    setAnimationKey(prev => prev + 1);
  }, []);

  const connectSSE = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const API_URL = process.env.NODE_ENV === 'production'
      ? '/api'
      : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');
    const sseUrl = `${API_URL}/photobooth/${slug}/events`;
    console.log('📡 [SSE] Conectando a:', sseUrl);
    
    const eventSource = new EventSource(sseUrl);
    eventSourceRef.current = eventSource;
    
    eventSource.onopen = () => {
      console.log('📡 [SSE] Conexión establecida');
    };
    
    eventSource.addEventListener('new-photo', (event) => {
      try {
        const photoData = JSON.parse(event.data);
        handleNewPhoto(photoData);
      } catch (err) {
        console.error('📡 [SSE] Error parseando evento:', err);
      }
    });
    
    eventSource.onerror = (err) => {
      console.error('📡 [SSE] Error de conexión:', err);
      eventSource.close();
      
      reconnectTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          connectSSE();
        }
      }, 5000);
    };
  }, [slug, handleNewPhoto]);

  useEffect(() => {
    loadSettingsFromStorage();
  }, [loadSettingsFromStorage]);

  useEffect(() => {
    loadProjectInfo();
    loadPhotos();
    connectSSE();
    
    return () => {
      isMountedRef.current = false;
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (videoTimeoutRef.current) {
        clearTimeout(videoTimeoutRef.current);
      }
    };
  }, [slug, loadProjectInfo, loadPhotos, connectSSE]);

  useEffect(() => {
    if (photos.length > 0) {
      preloadImages(photos, currentIndex);
    }
  }, [photos, currentIndex, preloadImages]);

  useEffect(() => {
    if (photos.length > 0) {
      startSlideshow();
    }
    return () => {
      if (slideshowIntervalRef.current) {
        clearInterval(slideshowIntervalRef.current);
        slideshowIntervalRef.current = null;
      }
      if (videoTimeoutRef.current) {
        clearTimeout(videoTimeoutRef.current);
        videoTimeoutRef.current = null;
      }
    };
  }, [photos.length, slideDuration, currentIndex]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isFullscreen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      switch(e.key) {
        case 'F1':
          e.preventDefault();
          setShowShortcuts(prev => !prev);
          break;
        case 'F2':
          e.preventDefault();
          setShowTitle(prev => {
            const newVal = !prev;
            saveSettings({ ...loadSettings(), showTitle: newVal });
            return newVal;
          });
          break;
        case 'F3':
          e.preventDefault();
          setShowQR(prev => {
            const newVal = !prev;
            saveSettings({ ...loadSettings(), showQR: newVal });
            return newVal;
          });
          break;
        case 'F4':
          e.preventDefault();
          setShowControls(prev => !prev);
          break;
        case 'Escape':
          setShowShortcuts(false);
          setShowControls(false);
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleTransitionChange = (value) => {
    setTransitionStrategy(value);
    saveSettings({ ...loadSettings(), transitionStrategy: value });
  };

  const handleDurationChange = (value) => {
    const num = parseInt(value, 10);
    if (num >= 1 && num <= 9) {
      setSlideDuration(num);
      saveSettings({ ...loadSettings(), slideDuration: num });
    }
  };

  const handleImageFitChange = (value) => {
    setImageFit(value);
    saveSettings({ ...loadSettings(), imageFit: value });
  };

  const startSlideshow = () => {
    if (slideshowIntervalRef.current) {
      clearInterval(slideshowIntervalRef.current);
      slideshowIntervalRef.current = null;
    }
    if (videoTimeoutRef.current) {
      clearTimeout(videoTimeoutRef.current);
      videoTimeoutRef.current = null;
    }
    
    if (photos.length === 0) return;
    
    const currentMedia = photos[currentIndex];
    const isVideo = currentMedia?.mediaType === 'video';
    
    if (isVideo) {
      // For videos, the video element will handle playback
      setIsVideoPlaying(true);
      return;
    }
    
    // For photos, use the normal slideshow timer
    setIsVideoPlaying(false);
    slideshowIntervalRef.current = setInterval(() => {
      setAnimationKey(prev => prev + 1);
      setCurrentIndex(prev => (prev + 1) % photos.length);
    }, slideDuration * 1000);
  };

  const handleVideoEnd = () => {
    setIsVideoPlaying(false);
    setAnimationKey(prev => prev + 1);
    setCurrentIndex(prev => (prev + 1) % photos.length);
  };

  const handleVideoError = () => {
    // Skip to next item if video fails to play
    setIsVideoPlaying(false);
    setAnimationKey(prev => prev + 1);
    setCurrentIndex(prev => (prev + 1) % photos.length);
  };

  // Ensure video plays when mounted
  useEffect(() => {
    const currentMedia = photos[currentIndex];
    if (currentMedia?.mediaType === 'video' && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, [currentIndex, photos]);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await containerRef.current?.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error('Error toggling fullscreen:', err);
    }
  };

  const getAnimationStyle = () => {
    const strategy = transitionStrategy;
    switch (strategy) {
      case 'fade':
        return { animation: `fadeIn 0.8s ease-out forwards` };
      case 'slide':
        return currentIndex % 2 === 0 
          ? { animation: `slideInLeft 0.8s ease-out forwards` }
          : { animation: `slideInRight 0.8s ease-out forwards` };
      case 'zoom':
        return { animation: `zoomIn 0.8s ease-out forwards` };
      case 'flip':
        return { animation: `flipIn 0.8s ease-out forwards` };
      default:
        return { animation: `fadeIn 0.8s ease-out forwards` };
    }
  };

  if (loading) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-black">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto"></div>
          <p className="mt-4 text-xl">Cargando...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-black">
        <div className="text-center text-white">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold mb-2">Error</h1>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  const currentPhoto = photos[currentIndex];

  return (
    <div 
      ref={containerRef}
      className="w-screen h-screen bg-black flex flex-col relative overflow-hidden"
      style={{
        background: projectInfo?.branding?.backgroundColor || '#000000'
      }}
    >
      {/* QR Code */}
      {showQR && (
        <div className="absolute top-4 right-4 z-20 flex flex-col items-center">
          <div className="bg-white p-3 rounded-xl shadow-xl flex items-center justify-center">
            <QRCodeSVG 
              value={projectInfo?.panelUrl || `${frontendUrl}/photobooth/${slug}`}
              size={100}
              level="M"
            />
          </div>
          <p className="text-white text-xs text-center mt-2 bg-black bg-opacity-50 rounded px-2 py-1">
            Escanea para subir foto
          </p>
        </div>
      )}

      {/* Project name */}
      {showTitle && (
        <div className="absolute top-4 left-4 z-20">
          <h1 
            className="text-2xl font-bold"
            style={{ color: projectInfo?.branding?.primaryColor || '#3B82F6' }}
          >
            {projectInfo?.projectName}
          </h1>
        </div>
      )}

      {/* Photo/Video Slideshow - Full screen */}
      {currentPhoto && (
        <div className="flex-1 w-full h-full flex items-center justify-center">
          {currentPhoto.mediaType === 'video' ? (
            <video
              ref={videoRef}
              key={`${currentPhoto.fileName}-${animationKey}`}
              src={`${backendUrl}${currentPhoto.url}`}
              autoPlay
              muted
              playsInline
              className={`w-full h-full object-${imageFit}`}
              style={getAnimationStyle()}
              onEnded={handleVideoEnd}
              onError={handleVideoError}
            />
          ) : (
            <img
              key={`${currentPhoto.fileName}-${animationKey}`}
              src={`${backendUrl}${currentPhoto.url}`}
              alt="Foto del evento"
              className={`w-full h-full object-${imageFit}`}
              style={getAnimationStyle()}
            />
          )}
          
          {(currentPhoto.uploaderName || currentPhoto.message) && (
            <div className="absolute bottom-20 left-0 right-0 text-center pointer-events-none px-4">
              <div className="inline-block bg-black bg-opacity-70 rounded-xl px-6 py-3 max-w-2xl mx-auto">
                {currentPhoto.uploaderName && (
                  <p className="text-white font-bold text-xl">
                    {currentPhoto.uploaderName}
                  </p>
                )}
                {currentPhoto.message && (
                  <p className="text-gray-200 text-lg mt-1">
                    "{currentPhoto.message}"
                  </p>
                )}
              </div>
            </div>
          )}
          
          {/* Video indicator */}
          {currentPhoto.mediaType === 'video' && (
            <div className="absolute top-4 right-4 px-3 py-1.5 bg-black bg-opacity-60 rounded-full text-white text-sm flex items-center">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
              </svg>
              Video
            </div>
          )}
          
          <div className="absolute bottom-4 left-4 px-3 py-1.5 bg-black bg-opacity-60 rounded-full text-white text-sm">
            {currentIndex + 1} / {photos.length}
          </div>
        </div>
      )}

      {/* No photos/videos message */}
      {photos.length === 0 && (
        <div className="flex-1 w-full h-full flex items-center justify-center">
          <div className="text-center text-gray-500">
            <div className="text-6xl mb-4">📷</div>
            <p className="text-xl">Aún no hay fotos ni videos</p>
            <p className="text-gray-400 mt-2">Las fotos y videos que se suban aparecerán aquí</p>
          </div>
        </div>
      )}

      {/* Control bar */}
      {showControls && (
        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-80 p-4 flex items-center justify-between z-30">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <span className="text-white text-sm">Efecto:</span>
              <select
                value={transitionStrategy}
                onChange={(e) => handleTransitionChange(e.target.value)}
                className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-lg text-sm cursor-pointer"
              >
                {Object.entries(TRANSITION_STRATEGIES).map(([key, value]) => (
                  <option key={key} value={key} className="text-black">
                    {value.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-white text-sm">Tiempo:</span>
              <input
                type="number"
                min="1"
                max="9"
                value={slideDuration}
                onChange={(e) => handleDurationChange(e.target.value)}
                className="bg-white bg-opacity-20 text-white w-16 px-3 py-2 rounded-lg text-sm text-center"
              />
              <span className="text-white text-sm">seg</span>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-white text-sm">Ajuste:</span>
              <select
                value={imageFit}
                onChange={(e) => handleImageFitChange(e.target.value)}
                className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-lg text-sm cursor-pointer"
              >
                <option value="contain" className="text-black">Contain</option>
                <option value="cover" className="text-black">Cover</option>
              </select>
            </div>
          </div>
          
          <button
            onClick={toggleFullscreen}
            className="p-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg text-white transition"
            title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
            type="button"
          >
            {isFullscreen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            )}
          </button>
        </div>
      )}

      {/* Keyboard shortcuts modal */}
      {showShortcuts && (
        <div className="absolute inset-0 z-40 bg-black bg-opacity-90 flex items-center justify-center">
          <div className="bg-gray-900 rounded-2xl p-8 max-w-md w-full mx-4">
            <h2 className="text-white text-2xl font-bold mb-6 text-center">Atajos de Teclado</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Mostrar/Ocultar ayuda</span>
                <kbd className="bg-gray-700 text-white px-3 py-1 rounded text-sm">F1</kbd>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Mostrar/Ocultar título</span>
                <kbd className="bg-gray-700 text-white px-3 py-1 rounded text-sm">F2</kbd>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Mostrar/Ocultar QR</span>
                <kbd className="bg-gray-700 text-white px-3 py-1 rounded text-sm">F3</kbd>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Mostrar/Ocultar controles</span>
                <kbd className="bg-gray-700 text-white px-3 py-1 rounded text-sm">F4</kbd>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Pantalla completa</span>
                <kbd className="bg-gray-700 text-white px-3 py-1 rounded text-sm">F11</kbd>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Cerrar</span>
                <kbd className="bg-gray-700 text-white px-3 py-1 rounded text-sm">ESC</kbd>
              </div>
            </div>
            <button
              onClick={() => setShowShortcuts(false)}
              className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-medium transition"
              type="button"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes fadeIn { 
            from { opacity: 0; } 
            to { opacity: 1; } 
          }
          @keyframes fadeOut { 
            from { opacity: 1; } 
            to { opacity: 0; } 
          }
          @keyframes slideInLeft { 
            from { transform: translateX(-100%); opacity: 0; } 
            to { transform: translateX(0); opacity: 1; } 
          }
          @keyframes slideInRight { 
            from { transform: translateX(100%); opacity: 0; } 
            to { transform: translateX(0); opacity: 1; } 
          }
          @keyframes slideOutLeft { 
            from { transform: translateX(0); opacity: 1; } 
            to { transform: translateX(-100%); opacity: 0; } 
          }
          @keyframes slideOutRight { 
            from { transform: translateX(0); opacity: 1; } 
            to { transform: translateX(100%); opacity: 0; } 
          }
          @keyframes zoomIn { 
            from { transform: scale(0.8); opacity: 0; } 
            to { transform: scale(1); opacity: 1; } 
          }
          @keyframes zoomOut { 
            from { transform: scale(1); opacity: 1; } 
            to { transform: scale(1.2); opacity: 0; } 
          }
          @keyframes flipIn { 
            from { transform: perspective(400px) rotateY(90deg); opacity: 0; } 
            to { transform: perspective(400px) rotateY(0); opacity: 1; } 
          }
          @keyframes flipOut { 
            from { transform: perspective(400px) rotateY(0); opacity: 1; } 
            to { transform: perspective(400px) rotateY(-90deg); opacity: 0; } 
          }
        `}
      </style>
    </div>
  );
};

export default PhotoBoothDisplay;
